import { z } from "zod";
import { CFG_COMMANDS, CFG_FAMILIES } from "./cfg-commands.ts";
import { Scope } from "./stuff.ts";

/**
 * Milestone 5, configs: a shelf of .cfg files every teammate can read line by line
 * before running them.
 */

export const CONFIG_CATEGORIES = ["crosshair", "viewmodel", "binds", "practice", "buy", "audio_video", "other"] as const;
export const ConfigCategory = z.enum(CONFIG_CATEGORIES);
export type ConfigCategory = z.infer<typeof ConfigCategory>;

/** A config is plain text: 64 KB is far more than any real one. */
export const MAX_CONFIG_BYTES = 64 * 1024;
export const MAX_CONFIGS_PER_SCOPE = 300;

const ConfigText = z
  .string()
  .max(MAX_CONFIG_BYTES, "A config is at most 64 KB.")
  .refine((t) => t.trim().length > 0, "The file is empty.")
  .refine((t) => !t.includes("\u0000"), "This is not a text file.");

/** The author's own words for a command, replacing the built-in explanation. */
const Overrides = z.record(z.string().trim().toLowerCase().min(1).max(64), z.string().trim().max(300)).default({});

export const ConfigMeta = z.object({
  title: z.string().trim().min(1, "Name the config.").max(60),
  description: z.string().trim().max(500).default(""),
  category: ConfigCategory,
  overrides: Overrides,
});
export type ConfigMeta = z.infer<typeof ConfigMeta>;

/** A file name from a title: "Pro crosshair" → pro_crosshair.cfg. */
export function cfgFileName(title: string): string {
  const base = title
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return `${base || "config"}.cfg`;
}

// ---------------------------------------------------------------- packs

/**
 * A pack: one main file plus the files it runs with `exec` (a spawn config that
 * loads one file per position, say). Names are as the game reads them from the
 * cfg folder: "spawn/ak.cfg" is run with `exec spawn/ak`.
 */
export const MAX_PACK_FILES = 20;
export const MAX_PACK_BYTES = 256 * 1024;
export const CfgFileName = z
  .string()
  .trim()
  .max(80)
  .transform((n) => n.replace(/\\/g, "/").replace(/^\/+/, ""))
  .transform((n) => (/\.cfg$/i.test(n) ? n : `${n}.cfg`))
  .refine((n) => /^[A-Za-z0-9_-]+(\/[A-Za-z0-9_-]+){0,2}\.cfg$/i.test(n), "A file name is letters, digits, _ or - (up to two folders), ending in .cfg.");
export const CfgFile = z.object({ name: CfgFileName, text: ConfigText });
export type CfgFile = z.infer<typeof CfgFile>;

const packKey = (name: string) => name.toLowerCase();

/** The main file and the pack's other files, checked together: names unique, size bounded. */
const PackFields = z
  .object({
    text: ConfigText,
    /** The main file's name; without one it comes from the title. */
    fileName: CfgFileName.optional(),
    files: z.array(CfgFile).max(MAX_PACK_FILES, `A pack holds at most ${MAX_PACK_FILES + 1} files.`).default([]),
  })
  .superRefine((p, ctx) => {
    const names = [...(p.fileName ? [p.fileName] : []), ...p.files.map((f) => f.name)].map(packKey);
    if (new Set(names).size !== names.length) ctx.addIssue({ code: "custom", path: ["files"], message: "Two files of the pack have the same name." });
    const bytes = p.text.length + p.files.reduce((n, f) => n + f.text.length, 0);
    if (bytes > MAX_PACK_BYTES) ctx.addIssue({ code: "custom", path: ["files"], message: "A pack is at most 256 KB in all." });
  });

export const CreateConfigInput = z.intersection(ConfigMeta.extend({ scope: Scope }), PackFields);
export type CreateConfigInput = z.input<typeof CreateConfigInput>;

export const ConfigVersionInput = PackFields;
export type ConfigVersionInput = z.input<typeof ConfigVersionInput>;

/** What `exec x` asks for, as a pack file name: quotes off, .cfg on, forward slashes. */
export function execTarget(arg: string | undefined): string | null {
  const a = (arg ?? "").trim().replace(/^"|"$/g, "").replace(/\\/g, "/").replace(/^\/+/, "");
  if (!a) return null;
  return /\.cfg$/i.test(a) ? a : `${a}.cfg`;
}

/** The pack file `exec arg` runs, when the pack has it. */
export function packFileFor(arg: string | undefined, names: string[]): string | null {
  const want = execTarget(arg);
  if (!want) return null;
  return names.find((n) => packKey(n) === packKey(want)) ?? null;
}

/** Every file an `exec` line of this text names. */
export function execTargets(text: string): string[] {
  const out: string[] = [];
  for (const line of parseCfg(text)) for (const st of line.statements) if (st.command.toLowerCase() === "exec") {
    const n = execTarget(st.args[0]);
    if (n) out.push(n);
  }
  return out;
}

/**
 * Of the files picked together, the one that runs the others: it execs some of
 * them and none of them execs it. Otherwise the first.
 */
export function guessMainFile(files: { name: string; text: string }[]): number {
  // Picked files come without their folder: "exec spawn/ak" matches ak.cfg too.
  const base = (n: string) => packKey(n).split("/").pop()!;
  const names = files.map((f) => packKey(f.name));
  const runs = files.map((f) => execTargets(f.text).filter((n) => names.some((x) => x === packKey(n) || base(x) === base(n))));
  const ran = new Set(runs.flat().map(base));
  const best = files.findIndex((_, i) => runs[i].length > 0 && !ran.has(base(names[i])));
  return best === -1 ? 0 : best;
}

/**
 * Files picked from a folder lose its subfolders: gives each the path the pack's
 * exec lines use for it ("ak.cfg" becomes "spawn/ak.cfg" when a file runs
 * `exec spawn/ak`), so the downloaded pack is laid out as the game expects.
 */
export function placeByExecs<T extends { name: string; text: string }>(files: T[]): T[] {
  const wanted = files.flatMap((f) => execTargets(f.text));
  return files.map((f) => {
    if (f.name.includes("/") || wanted.some((w) => packKey(w) === packKey(f.name))) return f;
    const path = wanted.find((w) => packKey(w).split("/").pop() === packKey(f.name));
    return path ? { ...f, name: path } : f;
  });
}

/** Lines and lines to check, over every file of a pack. */
export function packSummary(text: string, files: { text: string }[] = []): { lines: number; warnings: number } {
  return [text, ...files.map((f) => f.text)].map((t) => cfgSummary(t)).reduce((a, b) => ({ lines: a.lines + b.lines, warnings: a.warnings + b.warnings }));
}

export interface ConfigVersionSummary {
  version: number;
  createdAt: string;
  author: { id: string; name: string };
  lines: number;
}

export interface ConfigSummary {
  id: string;
  teamId: string | null;
  title: string;
  description: string;
  category: ConfigCategory;
  author: { id: string; name: string };
  version: number;
  lines: number;
  /** How many lines deserve a look (binds, unbindall, exec…). */
  warnings: number;
  /** 1 for a single file; more for a pack. */
  fileCount: number;
  updatedAt: string;
  canEdit: boolean;
}

export interface Config extends ConfigSummary {
  /** The main file's name, as downloaded. */
  fileName: string;
  text: string;
  /** The pack's other files (none for a single file). */
  files: CfgFile[];
  overrides: Record<string, string>;
  versions: ConfigVersionSummary[];
}

// ---------------------------------------------------------------- reading a file

export type CfgFlag = "bind" | "unbindall" | "exec" | "cheat" | "server" | "multi_action" | "alias" | "unknown";

export interface CfgStatement {
  /** 1-based line in the file. */
  line: number;
  command: string;
  args: string[];
  /** Built-in explanation, the author's own, or null when the command is not known. */
  explain: string | null;
  /** Where the explanation comes from: the dictionary, a family of settings, or the author. */
  source: "dictionary" | "family" | "author" | null;
  flags: CfgFlag[];
  /** The key a bind changes, lower case. */
  key?: string;
}

export interface CfgLine {
  line: number;
  raw: string;
  /** Text after // on this line: the author's note. */
  comment: string | null;
  statements: CfgStatement[];
}

/** Splits a line into statements and words, honouring quotes and // comments. */
function tokenize(raw: string): { statements: string[][]; comment: string | null } {
  const statements: string[][] = [];
  let words: string[] = [];
  let word = "";
  let inQuote = false;
  let started = false;
  const endWord = () => {
    if (started) words.push(word);
    word = "";
    started = false;
  };
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (inQuote) {
      if (ch === '"') inQuote = false;
      else word += ch;
      continue;
    }
    if (ch === "/" && raw[i + 1] === "/") {
      endWord();
      if (words.length) statements.push(words);
      return { statements, comment: raw.slice(i + 2).trim() };
    }
    if (ch === '"') {
      inQuote = true;
      started = true;
    } else if (ch === ";") {
      endWord();
      if (words.length) statements.push(words);
      words = [];
    } else if (/\s/.test(ch)) endWord();
    else {
      word += ch;
      started = true;
    }
  }
  endWord();
  if (words.length) statements.push(words);
  return { statements, comment: null };
}

/** What the dictionary says about a command: its own entry, else its family's. */
export function lookupCommand(command: string): { explain: string; source: "dictionary" | "family"; cheat: boolean; server: boolean } | null {
  const key = command.toLowerCase();
  const known = CFG_COMMANDS[key];
  if (known) return { explain: known.explain, source: "dictionary", cheat: !!known.cheat, server: !!known.server };
  const family = CFG_FAMILIES.find((f) => key.startsWith(f.prefix));
  if (family) return { explain: family.explain, source: "family", cheat: false, server: !!family.server };
  return null;
}

/** Movement and attack actions: CS2 no longer runs binds that chain several of them. */
const ACTION = /^[+-](attack|attack2|jump|duck|forward|back|moveleft|moveright|left|right|use|speed|sprint)$/i;

function readStatement(line: number, words: string[], overrides: Record<string, string>): CfgStatement {
  const [first, ...args] = words;
  const command = first.toLowerCase();
  const info = lookupCommand(command);
  const flags: CfgFlag[] = [];
  let key: string | undefined;
  if (command === "bind" && args.length >= 1) {
    flags.push("bind");
    key = args[0].toLowerCase();
    const inner = (args[1] ?? "").split(";").map((s) => s.trim()).filter(Boolean);
    if (inner.filter((s) => ACTION.test(s.split(/\s+/)[0])).length >= 2) flags.push("multi_action");
  }
  if (command === "unbindall") flags.push("unbindall");
  if (command === "exec") flags.push("exec");
  if (command === "alias") flags.push("alias");
  if (info?.cheat) flags.push("cheat");
  else if (info?.server) flags.push("server");
  if (!info && !overrides[command]) flags.push("unknown");
  const own = overrides[command];
  return {
    line,
    command: first,
    args,
    explain: own ?? info?.explain ?? null,
    source: own ? "author" : (info?.source ?? null),
    flags,
    ...(key ? { key } : {}),
  };
}

/** A config, line by line: each command with its explanation and what deserves attention. */
export function parseCfg(text: string, overrides: Record<string, string> = {}): CfgLine[] {
  const own = Object.fromEntries(Object.entries(overrides).map(([k, v]) => [k.toLowerCase(), v]));
  return text.split(/\r?\n/).map((raw, i) => {
    const { statements, comment } = tokenize(raw);
    return { line: i + 1, raw, comment, statements: statements.map((w) => readStatement(i + 1, w, own)) };
  });
}

/** Lines that deserve a look before running the file. */
export const WARNING_FLAGS: CfgFlag[] = ["bind", "unbindall", "exec", "cheat", "multi_action"];

export function cfgSummary(text: string): { lines: number; warnings: number } {
  const lines = parseCfg(text);
  return {
    lines: lines.filter((l) => l.statements.length > 0).length,
    warnings: lines.filter((l) => l.statements.some((s) => s.flags.some((f) => WARNING_FLAGS.includes(f)))).length,
  };
}

// ---------------------------------------------------------------- changing keys

/**
 * A key as the game names it in a bind: a letter as printed on the keyboard, f1,
 * mouse4, kp_enter, scancode20…, or one punctuation key (/ , . ' [ ] \\ - = `).
 */
export const CfgKey = z
  .string()
  .trim()
  .toLowerCase()
  .refine((k) => /^[a-z0-9_]{1,20}$/.test(k) || /^[/,.'[\]\\\-=`]$/.test(k), "Not a key name.");

/** Keys to change before downloading: the key in the file → the one to use. */
export const Rebinds = z.record(CfgKey, CfgKey).refine((r) => Object.keys(r).length <= 100, "Too many keys.");
export type Rebinds = z.infer<typeof Rebinds>;

/** Reads ?keys= (JSON); an empty or missing value is no change. */
export function parseRebinds(raw: string | undefined): Rebinds {
  if (!raw) return {};
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new z.ZodError([{ code: "custom", path: ["keys"], message: "Not a list of keys.", input: raw }]);
  }
  return Object.fromEntries(Object.entries(Rebinds.parse(value)).filter(([from, to]) => from !== to));
}

/** The statements of a line with where each starts, quotes and // comments honoured. */
function splitStatements(raw: string): { parts: string[]; rest: string } {
  const parts: string[] = [];
  let start = 0;
  let inQuote = false;
  for (let i = 0; i < raw.length; i++) {
    const ch = raw[i];
    if (ch === '"') inQuote = !inQuote;
    else if (!inQuote && ch === "/" && raw[i + 1] === "/") {
      parts.push(raw.slice(start, i));
      return { parts, rest: raw.slice(i) };
    } else if (!inQuote && ch === ";") {
      parts.push(raw.slice(start, i));
      start = i + 1;
    }
  }
  parts.push(raw.slice(start));
  return { parts, rest: "" };
}

const BIND_HEAD = /^(\s*(?:bind|unbind)\s+)("([^"]*)"|([^\s"]+))/i;

/** The file with its bind and unbind lines moved to other keys; everything else as it was. */
export function rebindCfg(text: string, rebinds: Rebinds): string {
  const map = new Map(Object.entries(rebinds).map(([a, b]) => [a.toLowerCase(), b.toLowerCase()]));
  if (map.size === 0) return text;
  return text
    .split(/(\r?\n)/)
    .map((line, i) => {
      if (i % 2 === 1) return line;
      const { parts, rest } = splitStatements(line);
      let changed = false;
      const out = parts.map((part) =>
        part.replace(BIND_HEAD, (whole, head: string, _k: string, quoted?: string, bare?: string) => {
          const to = map.get((quoted ?? bare ?? "").toLowerCase());
          if (!to) return whole;
          changed = true;
          return `${head}"${to}"`;
        }),
      );
      return changed ? out.join(";") + rest : line;
    })
    .join("");
}

export interface CfgBind {
  /** The key as written in the file, lower case. */
  key: string;
  file: string;
  line: number;
  /** What the key runs, as written. */
  action: string;
  /** The command it runs, explained, when the dictionary knows it. */
  explain: string | null;
}

/** Every bind of a config (all files of a pack), in file order. */
export function cfgBinds(files: { name: string; text: string }[], overrides: Record<string, string> = {}): CfgBind[] {
  const out: CfgBind[] = [];
  for (const f of files)
    for (const l of parseCfg(f.text, overrides))
      for (const st of l.statements) {
        if (!st.key || st.command.toLowerCase() !== "bind" || st.args.length < 2) continue;
        const action = st.args.slice(1).join(" ").trim();
        const words = action.split(/[;\s]+/).filter(Boolean);
        // "toggle bot_stop 1 0": what is toggled says more than toggle.
        const main = words[0]?.toLowerCase() === "toggle" && words[1] ? words[1] : words[0];
        const own = main ? overrides[main.toLowerCase()] : undefined;
        out.push({ key: st.key, file: f.name, line: st.line, action, explain: own ?? (main ? (lookupCommand(main)?.explain ?? null) : null) });
      }
  return out;
}

/**
 * Keys that end up running two different things after the changes: a key moved
 * onto one the file already uses, or two keys moved onto the same one.
 */
export function rebindClashes(binds: CfgBind[], rebinds: Rebinds): Map<string, string[]> {
  const byKey = new Map<string, Set<string>>();
  for (const b of binds) {
    const to = rebinds[b.key] ?? b.key;
    if (!byKey.has(to)) byKey.set(to, new Set());
    byKey.get(to)!.add(b.key);
  }
  return new Map([...byKey].filter(([, from]) => from.size > 1).map(([to, from]) => [to, [...from]]));
}

/** What a key does in CS2 out of the box, for a warning before taking it over. */
export const DEFAULT_KEY_USE: Record<string, string> = {
  w: "+forward",
  a: "+moveleft",
  s: "+back",
  d: "+moveright",
  space: "+jump",
  ctrl: "+duck",
  lshift: "+sprint",
  mouse1: "+attack",
  mouse2: "+attack2",
  e: "+use",
  r: "+reload",
  f: "+lookatweapon",
  g: "drop",
  b: "buymenu",
  tab: "+showscores",
  escape: "cancelselect",
  "1": "slot1",
  "2": "slot2",
  "3": "slot3",
  "4": "slot4",
  "5": "slot5",
  q: "lastinv",
  y: "messagemode",
  u: "messagemode2",
  z: "radio",
  mouse3: "player_ping",
  mwheelup: "invprev",
  mwheeldown: "invnext",
  "`": "toggleconsole",
};

// ---------------------------------------------------------------- what changed

export interface DiffLine {
  kind: "same" | "added" | "removed";
  text: string;
  /** Line number in the old file (same, removed) or the new one (added). */
  line: number;
}

/** Line-by-line changes between two versions (longest common subsequence). */
export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split(/\r?\n/);
  const b = after.split(/\r?\n/);
  const n = a.length;
  const m = b.length;
  // Files are small (64 KB at most): a full table is fine.
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) for (let j = m - 1; j >= 0; j--) lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) {
      out.push({ kind: "same", text: a[i], line: i + 1 });
      i++;
      j++;
    } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
      out.push({ kind: "removed", text: a[i], line: i + 1 });
      i++;
    } else {
      out.push({ kind: "added", text: b[j], line: j + 1 });
      j++;
    }
  }
  while (i < n) out.push({ kind: "removed", text: a[i], line: ++i });
  while (j < m) out.push({ kind: "added", text: b[j], line: ++j });
  return out;
}

// ---------------------------------------------------------------- built-in files

/** The practice config: a private server set up for learning utility. */
export const PRACTICE_CFG_NAME = "69ly_practice.cfg";
export const PRACTICE_CFG = `// 69LY Companion practice config: run it on your own server (Play › Practice with bots, or a private match).
// Load it with: exec 69ly_practice
// Every key below can be changed on the app's Configs page before you download it.
sv_cheats 1
bot_kick
mp_limitteams 0
mp_autoteambalance 0
mp_maxmoney 60000
mp_startmoney 60000
mp_buytime 9999
mp_buy_anywhere 1
mp_freezetime 0
mp_roundtime 60
mp_roundtime_defuse 60
mp_respawn_on_death_ct 1
mp_respawn_on_death_t 1
mp_ignore_round_win_conditions 1
// 1: the grenade comes back to your hand after each throw.
sv_infinite_ammo 1
ammo_grenade_limit_total 5
sv_grenade_trajectory_prac_pipreview 1
sv_grenade_trajectory_prac_trailtime 4
sv_showimpacts 2
sv_showimpacts_time 6
cl_versus_intro 0
mp_team_intro_time 0
mp_warmup_end
mp_restartgame 1

// Capture a position for a new stuff: press it, then paste the line in the app.
bind "p" "getpos"
// Remove every grenade in the air or on the ground, and their sounds.
bind "f1" "ent_fire smokegrenade_projectile kill; ent_fire molotov_projectile kill; ent_fire flashbang_projectile kill; ent_fire hegrenade_projectile kill; ent_fire decoy_projectile kill; stopsound"
// Throw your last grenade again, from where you threw it.
bind "f2" "sv_rethrow_last_grenade"
// Bots: add a CT, add a T, put one where you aim.
bind "f3" "bot_add_ct"
bind "f4" "bot_add_t"
bind "f5" "bot_place"
// Bots: stand still, crouch, copy your movement (each press switches on or off).
bind "f6" "toggle bot_stop 1 0"
bind "f7" "toggle bot_crouch 1 0"
bind "f8" "toggle bot_mimic 1 0"
// Hide the HUD but the kill feed, for clean pictures of a stuff.
bind "f9" "toggle cl_draw_only_deathnotices 1 0"
// Fly through walls.
bind "/" "noclip"
echo "69LY practice config loaded. Its keys are listed in the file and on the app's Configs page."
`;

// ---------------------------------------------------------------- builder

/** CS2 crosshair share codes: CSGO-xxxxx-xxxxx-xxxxx-xxxxx-xxxxx. */
export const CROSSHAIR_CODE = /^CSGO(-[A-Za-z0-9]{5}){5}$/;

/** The settings each part of the builder reads from the console, in order. */
export const BUILDER_PARTS = {
  viewmodel: ["viewmodel_fov", "viewmodel_offset_x", "viewmodel_offset_y", "viewmodel_offset_z", "viewmodel_presetpos", "cl_prefer_lefthanded"],
  bob: ["cl_bob_lower_amt", "cl_bobamt_lat", "cl_bobamt_vert", "cl_bobcycle"],
  radar: ["cl_radar_scale", "cl_hud_radar_scale", "cl_radar_always_centered", "cl_radar_rotate", "cl_radar_icon_scale_min", "cl_radar_square_with_scoreboard"],
  hud: ["hud_scaling", "cl_hud_color", "cl_hud_background_alpha", "cl_showloadout", "safezonex", "safezoney"],
} as const;
export type BuilderPart = keyof typeof BUILDER_PARTS;

/** The line to paste in the console: typing setting names prints their values. */
export const builderQuery = (part: BuilderPart) => BUILDER_PARTS[part].join("; ");

/**
 * Reads what the console printed after builderQuery: lines such as
 * `viewmodel_fov = 68` (CS2 may add flags after the value). Only the asked settings count.
 */
export function readConsoleValues(part: BuilderPart, printed: string): { name: string; value: string }[] {
  const wanted = new Set<string>(BUILDER_PARTS[part]);
  const out = new Map<string, string>();
  for (const raw of printed.split(/\r?\n/)) {
    const m = /^\s*(?:\[[^\]]*\]\s*)*"?([a-z_0-9]+)"?\s*(?:=|\s)\s*"?(-?[0-9]+(?:\.[0-9]+)?|true|false)"?/i.exec(raw);
    if (!m) continue;
    const name = m[1].toLowerCase();
    if (!wanted.has(name)) continue;
    let value = m[2].toLowerCase();
    if (value === "true") value = "1";
    if (value === "false") value = "0";
    // 68.000000 → 68
    if (/^-?\d+\.\d+$/.test(value)) value = String(Number(value));
    out.set(name, value);
  }
  return BUILDER_PARTS[part].filter((n) => out.has(n)).map((name) => ({ name, value: out.get(name)! }));
}
